import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  TextField,
  Grid,
  Chip,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Medication as MedicationIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  EventNote as EventNoteIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { loadPatientsMedHistory } from '../BaseURL';

const PRIMARY = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG = 'rgba(65,163,134,0.10)';
const BORDER = '#e5e9ef';
const MUTED = '#64748b';
const STRONG = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem',
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.6px'
};

const STATUS_STYLES = {
  served: { bg: 'rgba(65,163,134,0.12)', color: PRIMARY_DARK, border: 'rgba(65,163,134,0.35)' },
  dispensed: { bg: 'rgba(65,163,134,0.12)', color: PRIMARY_DARK, border: 'rgba(65,163,134,0.35)' },
  completed: { bg: 'rgba(65,163,134,0.12)', color: PRIMARY_DARK, border: 'rgba(65,163,134,0.35)' },
  pending: { bg: 'rgba(234,179,8,0.12)', color: '#a16207', border: 'rgba(234,179,8,0.35)' },
  cancelled: { bg: 'rgba(220,38,38,0.10)', color: '#b91c1c', border: 'rgba(220,38,38,0.30)' },
  default: { bg: '#f1f5f9', color: 'text.secondary', border: '#e2e8f0' }
};

const getStatusStyle = (status) => {
  if (!status) return STATUS_STYLES.default;
  const key = String(status).toLowerCase();
  return STATUS_STYLES[key] || STATUS_STYLES.default;
};

const MedicationHistory = ({ open, onClose, id }) => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('All');
  const [medicationHistory, setMedicationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const periodOptions = [
    { label: 'Today', value: 'Today' },
    { label: 'This Week', value: 'This Week' },
    { label: 'This Month', value: 'This Month' },
    { label: 'This Year', value: 'This Year' },
    { label: 'All', value: 'All' }
  ];

  const getDateRangeForPeriod = (period) => {
    const today = new Date();
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (period) {
      case 'Today':
        return { from: formatDate(today), to: formatDate(today) };
      case 'This Week': {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return { from: formatDate(startOfWeek), to: formatDate(endOfWeek) };
      }
      case 'This Month': {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { from: formatDate(startOfMonth), to: formatDate(endOfMonth) };
      }
      case 'This Year': {
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const endOfYear = new Date(today.getFullYear(), 11, 31);
        return { from: formatDate(startOfYear), to: formatDate(endOfYear) };
      }
      case 'All':
      default:
        return { from: '', to: '' };
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    const dateRange = getDateRangeForPeriod(period);
    setFromDate(dateRange.from);
    setToDate(dateRange.to);
  };

  const loadMedHistory = async () => {
    if (!id || !open) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(loadPatientsMedHistory, { id });

      if (response.data.success) {
        setMedicationHistory(response.data.results || []);
      } else {
        const errorMsg = response.data.message || 'Failed to load medication history';
        setError(errorMsg);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Failed to Load Data';

      setError(errorMessage);

      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && id) {
      loadMedHistory();
    } else {
      setMedicationHistory([]);
      setError(null);
    }
  }, [open, id]);

  const filteredAndGroupedMedicationHistory = useMemo(() => {
    let filteredMedications = medicationHistory;

    if (fromDate || toDate) {
      filteredMedications = medicationHistory.filter((med) => {
        const medDate = med.date;
        let includeRecord = true;
        if (fromDate && medDate < fromDate) includeRecord = false;
        if (toDate && medDate > toDate) includeRecord = false;
        return includeRecord;
      });
    }

    const grouped = {};
    filteredMedications.forEach((med) => {
      if (!grouped[med.date]) grouped[med.date] = [];
      grouped[med.date].push(med);
    });

    return grouped;
  }, [fromDate, toDate, medicationHistory]);

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setSelectedPeriod('All');
  };

  const hasActiveFilters = fromDate || toDate;
  const filteredEntryCount = Object.keys(filteredAndGroupedMedicationHistory).length;
  const totalEntryCount = [...new Set(medicationHistory.map(med => med.date))].length;
  const totalMedications = useMemo(
    () => Object.values(filteredAndGroupedMedicationHistory).reduce((acc, arr) => acc + arr.length, 0),
    [filteredAndGroupedMedicationHistory]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '700px',
          maxHeight: '92vh',
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: isDark
            ? 'linear-gradient(135deg, #0E0E14 0%, #14141C 100%)'
            : PRIMARY,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.75,
          borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MedicationIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.8px', opacity: 0.9 }}>
              PHARMACY · MEDICATION HISTORY
            </Typography>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.2 }}>
              Patient ID: {id || 'Unknown'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, bgcolor: 'background.default' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: PRIMARY }} />
            <Typography sx={{ ml: 2, color: MUTED, fontSize: '0.875rem' }}>
              Loading medication history…
            </Typography>
          </Box>
        )}

        {!loading && (
          <>
            {/* Filter Section */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderColor: BORDER,
                borderRadius: 1.5,
                bgcolor: 'background.paper'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <FilterIcon sx={{ fontSize: 16, color: PRIMARY }} />
                <Typography sx={{ ...LABEL_SX, color: PRIMARY_DARK }}>
                  Filter History
                </Typography>
                {hasActiveFilters && (
                  <Chip
                    label={`${filteredEntryCount} of ${totalEntryCount} day${totalEntryCount !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      ml: 'auto',
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: PRIMARY_BG,
                      color: PRIMARY_DARK,
                      border: `1px solid ${PRIMARY}40`
                    }}
                  />
                )}
              </Box>

              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>Period</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {periodOptions.map((option) => {
                      const active = selectedPeriod === option.value;
                      return (
                        <Button
                          key={option.value}
                          onClick={() => handlePeriodChange(option.value)}
                          disableElevation
                          sx={{
                            minWidth: 0,
                            px: 1.5,
                            py: 0.5,
                            fontSize: '0.74rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: active ? PRIMARY : BORDER,
                            bgcolor: active ? PRIMARY : 'white',
                            color: active ? 'white' : '#475569',
                            '&:hover': {
                              bgcolor: active ? PRIMARY_DARK : PRIMARY_BG,
                              borderColor: active ? PRIMARY_DARK : PRIMARY
                            }
                          }}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                  </Box>
                </Grid>

                <Grid item xs={6} md={2}>
                  <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>From</Typography>
                  <TextField
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setSelectedPeriod('');
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.825rem' } }}
                  />
                </Grid>

                <Grid item xs={6} md={2}>
                  <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>To</Typography>
                  <TextField
                    type="date"
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setSelectedPeriod('');
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.825rem' } }}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography sx={{ ...LABEL_SX, mb: 0.75, visibility: 'hidden' }}>Clear</Typography>
                  <Button
                    onClick={clearFilters}
                    disabled={!hasActiveFilters && selectedPeriod === 'All'}
                    startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                    fullWidth
                    size="small"
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      py: 0.85,
                      borderRadius: 1,
                      border: `1px solid ${BORDER}`,
                      color: MUTED,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        borderColor: PRIMARY,
                        color: PRIMARY_DARK,
                        bgcolor: PRIMARY_BG
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Summary strip */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: 1.25,
                bgcolor: hasActiveFilters && filteredEntryCount === 0 ? 'rgba(234,179,8,0.08)' : PRIMARY_BG,
                border: `1px solid ${hasActiveFilters && filteredEntryCount === 0 ? 'rgba(234,179,8,0.25)' : 'rgba(65,163,134,0.25)'}`
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: hasActiveFilters && filteredEntryCount === 0 ? '#a16207' : PRIMARY_DARK
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  color: hasActiveFilters && filteredEntryCount === 0 ? '#854d0e' : PRIMARY_DARK
                }}
              >
                {medicationHistory.length === 0
                  ? 'No medication records on file for this patient.'
                  : hasActiveFilters && filteredEntryCount === 0
                    ? 'No medication records match the selected filters.'
                    : `${totalMedications} medication${totalMedications !== 1 ? 's' : ''} across ${filteredEntryCount} day${filteredEntryCount !== 1 ? 's' : ''}${fromDate ? ` from ${formatDate(fromDate)}` : ''}${toDate ? ` to ${formatDate(toDate)}` : ''}`}
              </Typography>
            </Box>

            {/* Grouped medication tables */}
            <Box>
              {Object.keys(filteredAndGroupedMedicationHistory).length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    py: 6,
                    textAlign: 'center',
                    borderColor: BORDER,
                    borderStyle: 'dashed',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper'
                  }}
                >
                  <MedicationIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 1 }} />
                  <Typography sx={{ fontSize: '0.875rem', color: MUTED }}>
                    {medicationHistory.length === 0
                      ? 'No medication history available.'
                      : 'No records match the current filters.'}
                  </Typography>
                </Paper>
              ) : (
                Object.entries(filteredAndGroupedMedicationHistory)
                  .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
                  .map(([date, medications]) => (
                    <Paper
                      key={date}
                      variant="outlined"
                      sx={{
                        mb: 2,
                        borderColor: BORDER,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        bgcolor: 'background.paper'
                      }}
                    >
                      {/* Date Header */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: 1.25,
                          bgcolor: PRIMARY_BG,
                          borderBottom: `1px solid ${BORDER}`
                        }}
                      >
                        <EventNoteIcon sx={{ fontSize: 18, color: PRIMARY_DARK }} />
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: STRONG }}>
                          {formatDate(date)}
                        </Typography>
                        <Chip
                          label={`${medications.length} item${medications.length !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            ml: 'auto',
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: 'background.paper',
                            color: PRIMARY_DARK,
                            border: `1px solid ${PRIMARY}40`
                          }}
                        />
                      </Box>

                      <TableContainer
                        sx={{
                          maxHeight: medications.length > 5 ? 320 : 'auto'
                        }}
                      >
                        <Table size="small" stickyHeader={medications.length > 5}>
                          <TableHead>
                            <TableRow>
                              {['Medication', 'Route', 'Frequency', 'Duration', 'Qty', 'Prescribed By', 'Served By', 'Status'].map((h, i) => (
                                <TableCell
                                  key={h}
                                  align={i >= 4 ? 'center' : 'left'}
                                  sx={{
                                    ...LABEL_SX,
                                    bgcolor: 'background.default',
                                    borderBottom: `1px solid ${BORDER}`,
                                    py: 1
                                  }}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {medications.map((med, medIndex) => {
                              const statusStyle = getStatusStyle(med.status);
                              return (
                                <TableRow
                                  key={medIndex}
                                  hover
                                  sx={{
                                    '&:last-of-type td': { borderBottom: 0 }
                                  }}
                                >
                                  <TableCell sx={{ py: 1.1 }}>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: STRONG }}>
                                      {med.name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ py: 1.1 }}>
                                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                      {med.route}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ py: 1.1 }}>
                                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                      {med.frequency}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ py: 1.1 }}>
                                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                      {med.days}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center" sx={{ py: 1.1 }}>
                                    <Typography
                                      sx={{
                                        fontSize: '0.82rem',
                                        fontWeight: 700,
                                        color: STRONG,
                                        fontVariantNumeric: 'tabular-nums'
                                      }}
                                    >
                                      {med.quantity}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center" sx={{ py: 1.1 }}>
                                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                      {med.doc_name || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center" sx={{ py: 1.1 }}>
                                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                      {med.user || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center" sx={{ py: 1.1 }}>
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'inline-block',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 0.75,
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.4px',
                                        bgcolor: statusStyle.bg,
                                        color: statusStyle.color,
                                        border: `1px solid ${statusStyle.border}`
                                      }}
                                    >
                                      {med.status || '—'}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  ))
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${BORDER}`,
          bgcolor: 'background.paper'
        }}
      >
        <Button
          onClick={onClose}
          disableElevation
          sx={{
            textTransform: 'none',
            fontSize: '0.82rem',
            fontWeight: 600,
            px: 2.5,
            py: 0.85,
            borderRadius: 1,
            bgcolor: PRIMARY,
            color: 'white',
            '&:hover': { bgcolor: PRIMARY_DARK }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicationHistory;
